import {Component, OnInit, HostListener, ChangeDetectorRef} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {AuthenticationService} from '../../../core/services/authentication.service';
import {LoginRequest} from '../../../../view-models/login-request';
import {ActivatedRoute, Router} from '@angular/router';
import {TeamService} from '../../../core/services/team.service';
import {ITeamTemplate} from '../../../core/team-configuration/ITeamTemplate';
import {TeamTemplateComponent} from '../../../core/generic/team-template.component';
import {decode} from 'js-base64';
import { ToastMsg } from 'src/app/modules/core/models/ngprime/toastMsg';
import { ToastService } from 'src/app/modules/core/services/toast.service';
import { PortalBootstrapService } from '../../../core/services/portal-bootstrap.service';
import { Auth0ClientService } from '../../../core/services/auth0-client.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

@Component({
	selector: 'ticketing-auth-page',
	templateUrl: './auth-page.component.html',
	styleUrls: ['./auth-page.component.scss']
})
export class AuthPageComponent extends TeamTemplateComponent implements OnInit {
	public form: FormGroup;
	public template: ITeamTemplate;
	public isLoading: boolean = false;
	public loadingText: string = 'Loading';
	public auth0Enabled: boolean = false;
	// Landing-page instruction steps, re-emitting on language change (ngx-translate stream).
	public instructionSteps$: Observable<string[]>;
	private returnUrl: string;

	constructor(
		fb: FormBuilder,
		templateService: TeamService,
		private authenticationService: AuthenticationService,
		private router: Router,
		private toastService: ToastService,
		private activatedRoute: ActivatedRoute,
		private portalBootstrap: PortalBootstrapService,
		private auth0Client: Auth0ClientService,
		private translate: TranslateService,
		private changeDetectorRef: ChangeDetectorRef
	) {
		super(templateService);
		this.form = fb.group({
			email: ['', Validators.required],
			password: ['', Validators.required]
		});
		this.template = templateService.teamTemplate;
	}

	ngOnInit() {
		// Get returnUrl from route parameters or default to main page
		this.returnUrl = this.activatedRoute.snapshot.queryParams['returnUrl'] || `${this.template.teamId}/main`;
		this.auth0Enabled = this.portalBootstrap.auth0Enabled;

		if (this.auth0Enabled) {
			// Show the landing page (instructions + Sign In / Sign Up) instead of
			// auto-redirecting to Auth0, so users know they must sign up before signing in.
			this.instructionSteps$ = this.translate.stream('pages.auth0Landing.steps');
			return;
		}

		this.authenticationService.isLoggedIn().subscribe(isLoggedIn => {
			if (isLoggedIn) {
				this.router.navigate([this.returnUrl]);
			}
		});
	}

	// After Sign In / Sign Up we set isLoading=true and do a full-page redirect to Auth0. Pressing
	// the browser Back button restores this page from the back-forward cache (bfcache) with the
	// frozen spinner still showing — ngOnInit does not re-run on restore. Clearing isLoading alone
	// is NOT enough: change detection does not reliably run on a bfcache restore, so the overlay's
	// [hidden]="!isLoading" binding is never re-evaluated. We therefore clear the flag AND force a
	// change-detection pass. Keyed on isLoading being stuck (rather than event.persisted) so it
	// works regardless of how the browser reports the restore.
	@HostListener('window:pageshow')
	onPageShow() {
		if (this.isLoading) {
			this.isLoading = false;
			this.changeDetectorRef.detectChanges();
		}
	}

	public submit() {
		if (!this.form.valid) {
			this.form.markAllAsTouched();
			return;
		}
		this.isLoading = true;

		const request = <LoginRequest>this.form.getRawValue();
		this.authenticationService.login(request).subscribe(async (loginResult) => {
				if (!loginResult.isSuccess) {
					this.isLoading = false;
					return;
				}
				if (!this.authenticationService.isValidatedAccount(this.template.teamId)) {
					// Preserve returnUrl when redirecting to change password
					await this.router.navigate([`${this.template.teamId}/main/change-password`], { queryParams: { returnUrl: this.returnUrl } });
					return;
				}

				this.authenticationService.checkCustomersDataValidation()
					.subscribe((async customerDataValidationResult => {
						if (!customerDataValidationResult.isSuccess) {
							this.toastService.showError(new ToastMsg({ detail: customerDataValidationResult.errorMessage }));
						}
						if (customerDataValidationResult.data && customerDataValidationResult.data.length > 0) {
							const message = customerDataValidationResult.data.map(x => x.fieldName).join(', ');
							this.toastService.showWarning(new ToastMsg({ detail: message }));
							this.toastService.showWarning(new ToastMsg({ detail: 'There are fields that are not set in your profile, please consider going to profile and edit them' }));

							// Preserve returnUrl when redirecting to profile
							await this.router.navigate([`${this.template.teamId}/main/profile`], { queryParams: { returnUrl: this.returnUrl } });
						}
						else {
							await this.router.navigate([this.returnUrl]);
						}
						this.isLoading = false;
					}));
			});
	}

	// Auth0 landing-page action: start the Auth0 login (sign-in) experience.
	public signIn() {
		this.isLoading = true;
		this.auth0Client.loginWithRedirect(this.returnUrl).catch(() => {
			this.isLoading = false;
			this.toastService.showError(new ToastMsg({ detail: 'Unable to start sign-in. Please try again.' }));
		});
	}

	// Auth0 landing-page action: start the Auth0 signup experience (screen_hint=signup).
	public signUp() {
		this.isLoading = true;
		this.auth0Client.loginWithRedirect(this.returnUrl, true).catch(() => {
			this.isLoading = false;
			this.toastService.showError(new ToastMsg({ detail: 'Unable to start sign-up. Please try again.' }));
		});
	}

	register() {
		this.router.navigate(['steps'], {relativeTo: this.activatedRoute.parent});
	}

	forgotPassword() {
		this.router.navigate(['forgot-password'], {relativeTo: this.activatedRoute.parent});
	}
}
